import {observer} from 'mobx-react-lite'
import React, {useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {SprintStickerState} from 'yougile-model'
import {PermissionsChecker} from '@client/permissions-checker'
import {
  showDeleteConfirm
} from '@components/common/base/notifications'
import {Sticker} from '@components/common/base/sticker'
import {MessageBox} from '@components/message-box'
import {getStateColor} from '@components/sticker/sticker-menu/sticker-menu-string/utils'
import {PageService} from '@extensions/page-service'
import {SprintCtl} from '@extensions/sticker/controllers/sprint-ctl'
import {stickerCtl} from '@extensions/sticker/sticker'
import IconDelete from '../../../common/icons/icons-common/icon-delete.svg'
import IconEdit from '../../../common/icons/icons-common/icon-edit.svg'
import {
  StickerEditorState,
  StickerEditorStore,
  useStickerEditorStore
} from '../../sticker-editor-store'
import {useHoverHandlers} from '../../string-state-item/use-hover-handlers'
import {SprintStateEdit} from '../sprint-state-edit/sprint-state-edit'

const TwIcon = tw.div`
  text-third-old
  hover:text-action-default
  active:text-action-pressed
  cursor-pointer
  flex
  items-center
  pt-1
`

const TwText = tw.span`
  flex
  items-center
  leading-4
  truncate
  text-12
  ml-1
`

const sprintCtl = stickerCtl.Sprint

type UseActionHandlersRet = {
  edit: () => void
  del: () => void
}

type HandlerProps = {
  sprintStickerState: SprintStickerState
  store: StickerEditorStore
  state: StickerEditorState
}

const useActionHandlers = (props: HandlerProps): UseActionHandlersRet => {
  const {sprintStickerState, store, state} = props

  const edit = (): void => {
    const board = PageService.getCurrentBoard()
    if (!PermissionsChecker.checkEditBoardStickers(board)) {
      MessageBox.warn(Lang.msg.permission.noEditObject)
    } else {
      if (state.sticker) {
        store.setSprintState(
          sprintCtl.getState(state.sticker, sprintStickerState.id)
        )
      }
      store.setEditCurrentState(true)
      store.setSprintStateName(state.sprintState?.name || '')
    }
  }

  const del = async (): Promise<void> => {
    const board = PageService.getCurrentBoard()
    if (!PermissionsChecker.checkEditBoardStickers(board)) {
      MessageBox.warn(Lang.msg.permission.noEditObject)
    } else {
      if (await showDeleteConfirm(Lang.msg.confirmStickerStateDelete)) {
        if (state.sticker) {
          sprintCtl.removeState(state.sticker, sprintStickerState.id)
          store.setSprintStates(sprintCtl.listStates(state.sticker))
        }
        store.setStickerChanged(true)
      }
    }
  }

  return {edit, del}
}

export const SprintStateItem = observer(
  ({sprintStickerState}: {sprintStickerState: SprintStickerState}) => {
    const [state, store] = useStickerEditorStore()

    const {edit, del} = useActionHandlers({state, store, sprintStickerState})

    const interval = SprintCtl.formatInterval(sprintStickerState)
    const dates = interval ? (
      <span className="ml-4 font-semibold">{interval}</span>
    ) : undefined

    const [hovered, setHovered] = useState<boolean>(false)
    const {onMouseEnter, onMouseLeave} = useHoverHandlers({
      setHovered
    })
    const stickerColor = getStateColor(sprintStickerState, 'sprint')

    return (
      <div className="flex items-center justify-between">
        {state.editCurrentState &&
        state.sprintState?.id === sprintStickerState.id ? (
          <SprintStateEdit />
        ) : (
          <div
            className="flex flex-row"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          >
            <Sticker
              color={stickerColor}
              title={
                <TwText>
                  <span className="truncate">{sprintStickerState.name}</span>
                  {dates}
                </TwText>
              }
            />
            {hovered && (
              <div className="flex gap-18 ml-18">
                <TwIcon onClick={edit}>
                  <IconEdit />
                </TwIcon>
                <TwIcon onClick={del}>
                  <IconDelete />
                </TwIcon>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }
)
